import lightGallery from 'lightgallery'
import { app } from '../utils/store'

const getLightboxImgSrc = (img) => {
    if (app.webp && img.dataset.webp) return img.dataset.webp
    if (img.dataset.src) return img.dataset.src
    return img.currentSrc || img.src
}

/**
 * Lightbox per figure/immagini singole (es. icone dimensioni figurine).
 * Usare su elementi con [data-lightbox].
 *
 * @param {HTMLElement} element
 * @returns {object|null} istanza lightGallery o null
 */
export function initModalLightbox(element) {
    if (!element) {
        return null
    }

    const images = element.querySelectorAll('img')

    if (!images.length) {
        return null
    }

    if (element._lgHost) {
        return element._lgInstance || null
    }

    const buildDynamicEl = () =>
        Array.from(element.querySelectorAll('img')).map((img) => ({
            src: getLightboxImgSrc(img),
        }))

    const host = document.createElement('div')
    host.setAttribute('aria-hidden', 'true')
    host.hidden = true
    document.body.appendChild(host)
    element._lgHost = host

    const instance = lightGallery(host, {
        licenseKey: '0000-0000-000-0000',
        dynamic: true,
        dynamicEl: buildDynamicEl(),
        appendTo: 'body',
        closeOnTap: true,
        mousewheel: true,
        keypress: true,
        controls: images.length > 1,
        download: false,
        counter: images.length > 1,
        enableDrag: false,
        enableTouch: true,
        enableSwipe: true,
        subHtml: false,
        hideControlOnEnd: true,
        addClass: 'lg-modal-gallery lg-figurine-lightbox',
        mobileSettings: {
            showCloseIcon: true,
            controls: images.length > 1,
            counter: images.length > 1,
        },
    })

    host.addEventListener('lgBeforeOpen', () => {
        app.root?.classList.add('lg-modal-gallery-open')
    })

    host.addEventListener('lgAfterOpen', () => {
        const backdrop = document.querySelector('.lg-backdrop')
        if (backdrop) {
            backdrop._lgClose = () => instance.closeGallery()
            backdrop.addEventListener('click', backdrop._lgClose)
        }

        // Close on any click over the lightbox (image is pointer-events: none)
        const outer = document.querySelector('.lg-outer.lg-figurine-lightbox')
        if (outer) {
            outer._lgClose = () => instance.closeGallery()
            outer.addEventListener('click', outer._lgClose)
        }
    })

    host.addEventListener('lgBeforeClose', () => {
        const backdrop = document.querySelector('.lg-backdrop')
        if (backdrop?._lgClose) {
            backdrop.removeEventListener('click', backdrop._lgClose)
            delete backdrop._lgClose
        }

        const outer = document.querySelector('.lg-outer.lg-figurine-lightbox')
        if (outer?._lgClose) {
            outer.removeEventListener('click', outer._lgClose)
            delete outer._lgClose
        }
    })

    host.addEventListener('lgAfterClose', () => {
        app.root?.classList.remove('lg-modal-gallery-open')
    })

    const openAt = (index) => {
        instance.updateSlides(buildDynamicEl(), index)
        instance.openGallery(index)
    }

    images.forEach((img, index) => {
        img.style.cursor = 'zoom-in'
        img.addEventListener('click', () => openAt(index))
    })

    // Also open when clicking the figure itself (not only the img)
    element.addEventListener('click', (event) => {
        if (event.target.closest('img')) {
            return
        }

        openAt(0)
    })

    element._lgInstance = instance

    return instance
}

/**
 * Lightbox per un carousel Swiper con immagini.
 * Usare su elementi con [data-lightbox-gallery].
 * Drag = navigazione Swiper, click = apertura lightbox.
 *
 * @param {HTMLElement} swiperEl - il div.swiper
 * @returns {object|null} istanza lightGallery o null
 */
export function initGalleryLightbox(swiperEl) {
    if (!swiperEl) {
        return null
    }

    const images = swiperEl.querySelectorAll('.swiper-wrapper img')

    if (!images.length) {
        return null
    }

    const buildDynamicEl = () =>
        Array.from(swiperEl.querySelectorAll('.swiper-wrapper img')).map((img) => ({
            src: getLightboxImgSrc(img),
        }))

    if (swiperEl._lgGalleryHost) {
        return swiperEl._lgGalleryInstance || null
    }

    const host = document.createElement('div')
    host.setAttribute('aria-hidden', 'true')
    host.hidden = true
    document.body.appendChild(host)
    swiperEl._lgGalleryHost = host

    const instance = lightGallery(host, {
        licenseKey: '0000-0000-000-0000',
        dynamic: true,
        dynamicEl: buildDynamicEl(),
        appendTo: 'body',
        closeOnTap: true,
        mousewheel: true,
        keypress: true,
        controls: images.length > 1,
        download: false,
        counter: images.length > 1,
        enableDrag: false,
        enableTouch: true,
        enableSwipe: true,
        subHtml: false,
        hideControlOnEnd: true,
        addClass: 'lg-modal-gallery',
        mobileSettings: {
            showCloseIcon: true,
            controls: images.length > 1,
            counter: images.length > 1,
        },
    })

    host.addEventListener('lgBeforeOpen', () => {
        app.root?.classList.add('lg-modal-gallery-open')
    })

    host.addEventListener('lgAfterOpen', () => {
        const backdrop = document.querySelector('.lg-backdrop')
        if (backdrop) {
            backdrop._lgClose = () => instance.closeGallery()
            backdrop.addEventListener('click', backdrop._lgClose)
        }
    })

    host.addEventListener('lgBeforeClose', () => {
        const backdrop = document.querySelector('.lg-backdrop')
        if (backdrop?._lgClose) {
            backdrop.removeEventListener('click', backdrop._lgClose)
            delete backdrop._lgClose
        }
    })

    host.addEventListener('lgAfterClose', () => {
        app.root?.classList.remove('lg-modal-gallery-open')
    })

    swiperEl._lgGalleryInstance = instance

    images.forEach((img, index) => {
        img.style.cursor = 'zoom-in'

        img.addEventListener('click', () => {
            const swiper = swiperEl.swiper

            if (swiper && !swiper.allowClick) {
                return
            }

            // Aggiorna le immagini al momento dell'apertura (caricamento lazy)
            instance.updateSlides(buildDynamicEl(), index)
            instance.openGallery(index)
        })
    })

    return instance
}
